import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
	Card,
	CardBody,
	FormInput,
	Icon,
	Icons,
	StringHelpers,
	useLangContext,
} from 'carrier-fe';
import { CarouselSlide, CarouselSlideAction } from '../types';
import { isVideoType } from './CarouselMediaInput';

type Props = {
	slide: CarouselSlide;
	disabled: boolean;
	onAction: (action: CarouselSlideAction, slide: CarouselSlide) => void;
};

function SortableSlide({ slide, disabled, onAction }: Props) {
	const { crud, fields } = useLangContext();

	const { attributes, listeners, setNodeRef, transform, transition } =
		useSortable({ id: slide.id, disabled: disabled || !!slide.deleted_at });

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	const isDeleted = !!slide.deleted_at;

	const actionOptions = [
		...(slide.permissions?.update
			? [
					{
						label: StringHelpers.title(
							crud?.buttons?.edit?.default || 'Edit'
						),
						value: 'edit',
					},
			  ]
			: []),
		...(slide.permissions?.delete
			? [
					{
						label: StringHelpers.title(
							crud?.buttons?.archive?.default || 'Archive'
						),
						value: 'archive',
					},
			  ]
			: []),
		...(slide.permissions?.restore
			? [
					{
						label: StringHelpers.title(
							crud?.buttons?.restore?.default || 'Restore'
						),
						value: 'restore',
					},
			  ]
			: []),
	];

	return (
		<div
			ref={setNodeRef}
			style={style}
			className="mb-3"
		>
			<Card>
				<CardBody className="d-flex align-items-center">
					<div
						className="d-flex align-items-center border-end pe-4"
						style={{ cursor: isDeleted ? 'not-allowed' : 'grab' }}
						{...(isDeleted ? {} : attributes)}
						{...(isDeleted ? {} : listeners)}
						aria-label="Drag to reorder"
					>
						<Icon icon={Icons.DRAG_INDICATOR} />
					</div>
					<div className="ms-4" style={{ width: 160 }}>
						{slide.image?.url ? (
							isVideoType(slide.image.type) ? (
								<video
									src={slide.image.url}
									muted
									playsInline
									preload="metadata"
									style={{
										width: '100%',
										height: 80,
										objectFit: 'cover',
										borderRadius: 4,
										background: '#000',
									}}
								/>
							) : (
								<img
									src={slide.image.url}
									alt=""
									style={{
										width: '100%',
										height: 80,
										objectFit: 'cover',
										borderRadius: 4,
									}}
								/>
							)
						) : (
							<div
								className="bg-light d-flex align-items-center justify-content-center text-muted"
								style={{
									width: '100%',
									height: 80,
									borderRadius: 4,
								}}
							>
								<Icon icon={Icons.IMAGE} />
							</div>
						)}
					</div>
					<div className="flex-fill ms-4">
						<div
							className="text-uppercase fw-bold"
							style={{ color: '#1E82E6', fontSize: 12 }}
						>
							{StringHelpers.title(fields?.order || 'Order')}: {slide.order + 1}
						</div>
						<div className="text-break">
							{slide.url ? (
								<a
									href={slide.url}
									target="_blank"
									rel="noopener noreferrer"
									onClick={(e) => e.stopPropagation()}
								>
									{slide.url}
								</a>
							) : (
								<span className="text-muted">
									{fields?.no_link || 'No link'}
								</span>
							)}
						</div>
						{isDeleted && (
							<div className="text-danger small mt-1">
								{StringHelpers.title(
									fields?.archived || 'Archived'
								)}
							</div>
						)}
					</div>
					<div style={{ width: 200 }}>
						<FormInput
							type="select"
							label={StringHelpers.title(
								fields?.actions || 'Action'
							)}
							placeholder={StringHelpers.title(
								fields?.select || 'Select'
							)}
							name={`actions-${slide.id}`}
							value=""
							onChange={(value) => {
								if (typeof value === 'string' && value) {
									onAction(
										value as CarouselSlideAction,
										slide
									);
								}
							}}
							options={actionOptions}
							noMargin
						/>
					</div>
				</CardBody>
			</Card>
		</div>
	);
}

export default SortableSlide;
